/**
 * Module-level registry of pod-window hit rectangles for the building
 * exterior scene.  Mirror of ``podRoomHitRects`` (which registers
 * circular agent hit areas inside the pod room).
 *
 * Why a singleton?  The Pixi scene paints pod windows in canvas-space,
 * but the DOM tooltip overlay (``BuildingTooltipLayer``) needs to know
 * where each pod sits to translate ``pointermove`` events into "user
 * is hovering pod_moex_qwen_plus".
 *
 * Threading the layout through React props or the Zustand store would
 * trigger a re-render on every resize / pod-roster change -- wasteful
 * because hover hit-tests are pull-based, not push-based.  A
 * module-level ref keeps things simple: scene writes on rebuild,
 * overlay reads on mousemove.
 *
 * Coordinates are in CSS pixels relative to the host ``<div>`` that
 * contains the Pixi canvas (which uses ``autoDensity`` so Pixi's
 * pixel-space already matches CSS pixels -- no DPI conversion needed).
 */

export interface BuildingPodHitRect {
  /** Stable pod id (e.g. ``pod_moex_qwen_plus``).  Matches ``ScenePod.podId``. */
  podId: string;
  /** Pod display name, used as the tooltip header.  Cached here so the
   *  overlay doesn't have to look it up in the store on every hover. */
  name: string;
  /** Host-relative top-left corner. */
  x: number;
  y: number;
  /** Pod-window size. */
  w: number;
  h: number;
}

let _rects: ReadonlyArray<BuildingPodHitRect> = [];

/** Replace the registry atomically so the overlay never observes a
 * partially-updated list. */
export function setBuildingPodHitRects(
  rects: ReadonlyArray<BuildingPodHitRect>,
): void {
  _rects = rects;
}

export function getBuildingPodHitRects(): ReadonlyArray<BuildingPodHitRect> {
  return _rects;
}

/** Clears the registry when leaving the building view so a stale rect
 *  list doesn't outlive the scene that produced it. */
export function clearBuildingPodHitRects(): void {
  _rects = [];
}

/** Find the pod-window rect containing ``(x, y)`` (CSS pixels,
 * host-relative).  Returns ``null`` for misses.  When multiple windows
 * would match -- shouldn't normally happen, but defensively for layout
 * bugs -- returns the topmost (smaller ``y``) match. */
export function hitTestBuildingPod(
  x: number,
  y: number,
): BuildingPodHitRect | null {
  let best: BuildingPodHitRect | null = null;
  for (const rect of _rects) {
    if (
      x >= rect.x &&
      x <= rect.x + rect.w &&
      y >= rect.y &&
      y <= rect.y + rect.h
    ) {
      if (!best || rect.y < best.y) best = rect;
    }
  }
  return best;
}
